import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';

import { useUrlForPath } from 'src/hooks/useUrlForDomain';
import { useDeps } from 'src/hooks/useDeps';
import { ProductPrice, ProductType } from 'src/types/global-types';
import { ProductDetail } from 'src/components/ProductDetail/ProductDetail';
import type { DataSheet, Product, Specification } from 'src/components/ProductDetail/utils/product-detail.types';
import { Reviews } from 'src/components/Reviews/reviews.types';
import type { ProductAttrs } from 'src/components/ProductDetail/utils/product-attrs.types';
import { NostoContextProvider } from 'src/components/Nosto/NostoProvider';
import {
    ConfigurableOptionsContext,
    ConfigurableOptionsProvider,
    ConfigurableOptionsMachine,
} from 'src/components/ConfigurableOptions';
import { LoaderMinHeight } from 'src/components/Helpers/Skeleton';

import { viewProduct } from '../../features/product-store/actions';

type ProductDetailWrapperProps = {
    attrs: ProductAttrs;
    benefits: string[];
    dataSheets: DataSheet[];
    pathname: string;
    price: ProductPrice;
    product: Product;
    reviews: Reviews | null;
    specifications: Specification[];
    isComingSoon?: boolean;
};

export const ProductDetailWrapper: React.FC<ProductDetailWrapperProps> = React.memo((props) => {
    const dispatch = useDispatch();
    const { contact } = useDeps();
    const url = useUrlForPath(props.product.url_key || '');
    const firstImage = props.product.media_entries[0];
    const preload = firstImage && firstImage.medium_url;
    const type = props.product.type;
    const isComingSoon = props.isComingSoon;

    useEffect(() => {
        dispatch(
            viewProduct({
                ...props.product,
                ...props,
            }),
        );
    }, [dispatch, props]);

    /**
     * If the product is a simple product or coming soon show the simple product detail
     * All configurable options get removed when viewing a coming soon product so this approach is easier then applying logic to the ConfigurableWrapper
     * in short the Coming soon UI is the same for all prodiuct types which is the Simple Product
     */
    return (
        <ProductWrapper product={props.product} url={url} contactName={contact.name} preload={preload}>
            {(type === ProductType.SimpleProduct || isComingSoon) && <SimpleWrapper {...props} />}

            {type === ProductType.ConfigurableProduct && !isComingSoon && (
                <ConfigurableOptionsProvider pathname={props.pathname}>
                    <ConfigurableWrapper {...props} />
                </ConfigurableOptionsProvider>
            )}
        </ProductWrapper>
    );
});

type ProductWrapperProps = {
    product: Product;
    url: string;
    contactName: string;
    children: React.ReactNode;
    preload: string;
};

function ProductWrapper(props: ProductWrapperProps) {
    return (
        <>
            <Helmet titleTemplate={`%s | ${props.contactName}`}>
                <title>{props.product.meta_title}</title>
                <meta name="description" content={props.product.meta_description} />
                <meta property="og:title" content={props.product.meta_title} />
                <meta property="og:description" content={props.product.meta_description} />
                <meta property="og:image" content={props.product.small_image} />
                <link rel="canonical" href={props.url} />
                {props.preload && <link rel="preload" href={props.preload} as="image" />}
            </Helmet>
            <NostoContextProvider
                nostoData={{
                    type: ['product', 'addToBasket'],
                    productId: props.product.id,
                }}
                pageType="product"
            >
                {props.children}
            </NostoContextProvider>
        </>
    );
}

function SimpleWrapper(props: ProductDetailWrapperProps) {
    return (
        <ProductDetail
            testId="product-detail-page"
            product={props.product}
            price={props.price}
            reviews={props.reviews}
            specifications={props.specifications}
            dataSheets={props.dataSheets}
            benefits={props.benefits}
            attrs={props.attrs}
            isComingSoon={props.isComingSoon}
        />
    );
}

function ConfigurableWrapper(props: ProductDetailWrapperProps) {
    const { isLoading, configurableOptions, selectedVariant, setSelectedVariant, selectedOptions } =
        useContext(ConfigurableOptionsContext);

    if (isLoading) {
        return <LoaderMinHeight />;
    }

    // Destructure selectedVariant product properties or fallback to props.product properties
    const {
        id = String(props.product.id),
        description = props.product.description,
        sku = props.product.sku,
        type = props.product.type,
        media_entries = props.product.media_entries,
        attrs = props.attrs,
        price = props.price,
        specifications = props.specifications,
        dataSheets = props.dataSheets,
        benefits = props.benefits,
        labels = props.product.labels,
        subTitle = props.product.subTitle,
        promotions = undefined,
    } = selectedVariant?.product ?? {};

    // Create a new product object with the selected Variant properties if available
    const product = {
        ...props.product,
        subTitle,
        description,
        sku,
        type,
        media_entries,
        labels,
        promotions,
        id: id as string,
    };

    return (
        <ProductDetail
            testId="product-detail-configurable-page"
            parentSku={props.product.sku}
            product={product}
            price={price}
            attrs={attrs}
            reviews={props.reviews}
            specifications={specifications}
            dataSheets={dataSheets}
            benefits={benefits}
            configurableOptions={
                configurableOptions && (
                    <ConfigurableOptionsMachine
                        configurableOptions={configurableOptions}
                        setSelectedVariant={setSelectedVariant}
                        selectedVariant={selectedVariant}
                        selectedOptions={selectedOptions}
                    />
                )
            }
        />
    );
}
